import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  WritableSignal,
  inject,
} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {MatMenuModule} from '@angular/material/menu';
import {Subscription} from 'rxjs';
import {
  OphButtonGroupComponent,
  OphButtonGroupOption,
} from 'src/app/shared/design/oph-button-group/oph-button-group.component';
import {OphCheckboxComponent} from 'src/app/shared/design/oph-checkbox/oph-checkbox.component';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphInputsModule} from 'src/app/shared/design/oph-inputs/oph-inputs.module';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {ProjectTaskDialogPanels} from '../project-task-dialog-constants';
import {TaskTriggerSelectionComponent} from '../../trigger-selection/task-trigger-selection.component';
import {ProjectsTaskDateTimeButtonComponent} from '../date-time-button/projects-task-date-time-button.component';
import {ProjectsTaskDropdownSelectorButtonComponent} from '../dropdown-selector-button/projects-task-dropdown-selector-button.component';
import {ProjectsTaskInputSelectorButtonComponent} from '../input-selector-button/projects-task-input-selector-button.component';
import {ProjectsTaskTriggersComponent} from '../triggers/projects-task-triggers.component';

@Component({
  selector: 'projects-task-schedule',
  standalone: true,
  imports: [
    OphButtonGroupComponent,
    PipesModule,
    OphIconModule,
    ProjectsTaskDropdownSelectorButtonComponent,
    ProjectsTaskDateTimeButtonComponent,
    OphInputsModule,
    TaskTriggerSelectionComponent,
    ProjectsTaskInputSelectorButtonComponent,
    OphCheckboxComponent,
    MatMenuModule,
    ProjectsTaskTriggersComponent,
  ],
  templateUrl: './projects-task-schedule.component.html',
  styleUrl: './projects-task-schedule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.edit-mode]': '!newTask',
  },
})
export class ProjectsTaskScheduleComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() newTask: boolean;
  @Input() isSchedulePanelValid!: WritableSignal<boolean>;
  @Input() validationErrors: Record<string, boolean>;
  @Input() hasValidTrigger: boolean = false;
  @Input() isTriggerPanelValid: boolean = false;

  @Output() changePanel = new EventEmitter<{panel: ProjectTaskDialogPanels; dateTimeControlName?: string}>();
  @Output() newTrackerClick = new EventEmitter<void>();

  private subscriptions = new Subscription();
  private tasksService = inject(TasksService);

  ProjectTaskDialogPanels = ProjectTaskDialogPanels;
  numberOptions: number[] = Array.from({length: 8}, (_, i) => i + 1).concat([12, 24, 48, 72]);
  repeatTypes: string[] = ['hours', 'weeks', 'days'];

  // Store the original complete list of repeat ends in options
  private originalRepeatEndsInOptions: string[] = [
    'On Repeat', // Special option for ending on repeat
    // Generate "1 hour", "2 hours", etc. up to 12 hours with proper singular/plural grammar
    ...Array.from({length: 12}, (_, i) => i + 1).map(num => `${num} hour${num > 1 ? 's' : ''}`),
    // Add larger hour options (24, 48, 72 hours) - all plural
    ...[24, 48, 72].map(num => `${num} hours`),
  ];

  // Filtered options based on minimum repeat interval
  repeatEndsInOptions: string[] = [...this.originalRepeatEndsInOptions];
  repeatEndsInDisplay: string = 'On Repeat';

  get scheduleControl(): AbstractControl {
    return this.form.get('schedule');
  }

  get expirationTypeControl(): AbstractControl {
    return this.scheduleControl.get('expirationType');
  }

  viewOptions: OphButtonGroupOption[] = [
    {display: 'no repeat', name: 'doNotRepeat'},
    {display: 'repeats', name: 'repeats'},
    {display: 'trigger', name: 'trigger'},
  ];

  ngOnInit() {
    this.subscriptions.add(
      this.scheduleControl.valueChanges.subscribe(value => {
        this.checkScheduleCompleteness(value);
        this.setRepeatEndsInDisplay(value.repeatEndsIn);

        // Recalculate minimum interval when schedule values change
        if (value.scheduleType === 'repeats') {
          this.calculateMinimumRepeatInterval();
        }
      })
    );

    this.checkScheduleCompleteness(this.scheduleControl.value);
    this.setRepeatEndsInDisplay(this.scheduleControl.get('repeatEndsIn').value);

    // Calculate initial minimum interval if it's a repeating task
    if (this.scheduleControl.value.scheduleType === 'repeats') {
      this.calculateMinimumRepeatInterval();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setRepeatEndsInDisplay(value: string) {
    if (value === 'onRepeat') {
      this.repeatEndsInDisplay = 'On Repeat';
    } else {
      this.repeatEndsInDisplay = value;
    }
  }

  private checkScheduleCompleteness(schedule: any) {
    const {scheduleType, repeatType, repeatDays, cycleEndsType, cycleEndsTrigger} = schedule;
    if (scheduleType !== 'repeats') {
      this.isSchedulePanelValid.set(true);
      return;
    }
    if (repeatType === 'weeks' && repeatDays.length === 0) {
      this.isSchedulePanelValid.set(false);
      return;
    }
    if (cycleEndsType === 'trigger') {
      const isTriggerComplete = this.tasksService.checkIfTriggerIsComplete(cycleEndsTrigger);
      this.isSchedulePanelValid.set(isTriggerComplete);
      return;
    }
    this.isSchedulePanelValid.set(true);
  }

  onScheduleType(value: string) {
    if (value !== 'doNotRepeat') {
      this.form.get('deleteWhenComplete')?.setValue(false);
    }
    this.scheduleControl.get('scheduleType')?.setValue(value);
  }

  onScheduleOnDateType(value: string) {
    this.scheduleControl.get('scheduleOnDateType')?.setValue(value);
    if (value === 'date') {
      this.changePanel.emit({panel: ProjectTaskDialogPanels.DateTimePicker, dateTimeControlName: 'scheduleOnDate'});
    }
  }

  onExpirationType(value: string) {
    if (value === this.expirationTypeControl.value && value !== 'date') {
      return;
    }
    this.scheduleControl.get('expirationType')?.setValue(value);
    if (value === 'date') {
      this.changePanel.emit({panel: ProjectTaskDialogPanels.DateTimePicker, dateTimeControlName: 'expirationDate'});
    }
  }

  onCycleEndsType(value: string) {
    this.scheduleControl.get('cycleEndsType')?.setValue(value);
  }

  onRemoveCycleEndsTrigger() {
    this.scheduleControl.get('cycleEndsTrigger')?.setValue(this.tasksService.getTriggerFormGroup().value);
  }

  onRepeatEndsInChange(value: string) {
    this.scheduleControl.get('repeatEndsIn')?.setValue(value === 'On Repeat' ? 'onRepeat' : value);
  }

  onRepeatTypeChange(value: {dropdownOne: string; dropdownTwo: string}) {
    this.calculateMinimumRepeatInterval();
  }

  /**
   * Parses hour value from repeat ends in option string
   * Returns null for "On Repeat" option, otherwise extracts the number of hours
   */
  private parseHoursFromOption(option: string): number | null {
    if (option === 'On Repeat') {
      return null;
    }

    // Extract number from strings like "1 hour", "2 hours", "24 hours"
    const match = option.match(/^(\d+)\s+hour/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Calculates the minimum time interval between repeat cycles and filters repeatEndsInOptions
   */
  private calculateMinimumRepeatInterval(): void {
    const minimumHours = this.getMinimumRepeatHours();
    this.filterRepeatEndsInOptions(minimumHours);
  }

  /**
   * Calculates minimum hours between repeats based on schedule configuration
   */
  private getMinimumRepeatHours(): number | null {
    const {repeatEvery = 1, repeatType, repeatDays = []} = this.scheduleControl.value;
    const parsedRepeatEvery = parseInt(repeatEvery) || 1;

    switch (repeatType) {
      case 'days':
        return parsedRepeatEvery * 24;

      case 'weeks':
        if (repeatDays.length === 0) return null;
        return repeatDays.length === 1
          ? parsedRepeatEvery * 7 * 24
          : this.calculateMinGapBetweenDays(repeatDays, parsedRepeatEvery);

      default:
        return null;
    }
  }

  /**
   * Calculates minimum gap in hours between selected days
   */
  private calculateMinGapBetweenDays(repeatDays: number[], repeatEvery: number): number {
    const sortedDays = [...repeatDays].sort((a, b) => a - b);

    return Math.min(
      ...sortedDays.map((day, index) => {
        const nextDay = sortedDays[(index + 1) % sortedDays.length];
        const isWrapAround = index === sortedDays.length - 1;

        const gapDays = isWrapAround ? 7 - day + nextDay + (repeatEvery - 1) * 7 : nextDay - day;

        return gapDays * 24;
      })
    );
  }

  /**
   * Filters repeat end options based on minimum hours
   */
  private filterRepeatEndsInOptions(minimumHours: number | null): void {
    if (minimumHours === null) {
      this.repeatEndsInOptions = [...this.originalRepeatEndsInOptions];
      return;
    }

    this.repeatEndsInOptions = this.originalRepeatEndsInOptions.filter(option => {
      const optionHours = this.parseHoursFromOption(option);
      return optionHours === null || optionHours <= minimumHours;
    });
  }
}
